import PropTypes from 'prop-types';
import React, { memo, useCallback } from 'react';

import { SelectorGrid, StyledSelector } from './SelectorGroup.styles';

/**
 * Renders a row of equally-sized selector buttons that behave like a toggle
 * group. Selected selectors use the contained/primary style; unselected
 * selectors use the outlined style. Supports single or multi-select.
 */
const SelectorGroup = ({
  options,
  value,
  onChange,
  multiple = false,
  disabled = false,
  columns,
  ariaLabel,
}) => {
  const isSelected = useCallback(
    (optionValue) =>
      multiple ? (value ?? []).includes(optionValue) : value === optionValue,
    [multiple, value],
  );

  const handleSelect = useCallback(
    (optionValue) => {
      if (multiple) {
        const currentValues = value ?? [];
        const nextValues = currentValues.includes(optionValue)
          ? currentValues.filter((entry) => entry !== optionValue)
          : [...currentValues, optionValue];
        onChange(nextValues);
        return;
      }
      onChange(value === optionValue ? null : optionValue);
    },
    [multiple, onChange, value],
  );

  return (
    <SelectorGrid
      role="group"
      aria-label={ariaLabel}
      $columns={columns ?? options.length}
    >
      {options.map((option) => {
        const selected = isSelected(option.value);
        return (
          <StyledSelector
            key={option.value}
            type="button"
            variant={selected ? 'contained' : 'outlined'}
            disabled={disabled || option.disabled}
            aria-pressed={selected}
            onClick={() => handleSelect(option.value)}
          >
            {option.label}
          </StyledSelector>
        );
      })}
    </SelectorGrid>
  );
};

SelectorGroup.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.node.isRequired,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ),
  ]),
  onChange: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  columns: PropTypes.number,
  ariaLabel: PropTypes.string,
};

export default memo(SelectorGroup);
